import type { RadioOption } from '@/components/shared/RadioField';
import type { OptionGroup } from '@/components/ui/DropDown';
import { PlayerRepresentation } from '@/containers/vdr/setup/types';
import {
  ApplicationRequestStatus,
  ApplicationStatus,
  ApplicationType,
  ContractStatus,
  ContractType,
  FinancierType,
  FinancingType,
} from '@/generated/graphql';

export const yesNoOptions: RadioOption<'true' | 'false'>[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

export function getYesNoOptions(translate: any): RadioOption<'true' | 'false'>[] {
  return [
    { value: 'true', label: translate('labels.yes') },
    { value: 'false', label: translate('labels.no') },
  ];
}

export const currenciesList = [
  { value: 'GBP', label: 'GBP (£) - British Pound' },
  { value: 'EUR', label: 'EUR (€) - Euro' },
  { value: 'USD', label: 'USD ($) - US Dollar' },
  { value: 'MXN', label: 'MXN (Mex$) - Mexican Peso' },
  { value: 'ARS', label: 'ARS (ARS) - Argentine Peso' },
  { value: 'CHF', label: 'CHF (Fr.) - Swiss Franc' },
  { value: 'TRY', label: 'TRY (₺) - Turkish Lira' },
  { value: 'AED', label: 'AED (د.إ) - UAE Dirham' },
  { value: 'SAR', label: 'SAR (SR) - Saudi Riyal' },
  { value: 'BRL', label: 'BRL (R$) - Brazilian Real' },
  { value: 'QAR', label: 'QAR (QAR) - Qatari Riyal' },
] as const;

export type Currency = (typeof currenciesList)[number];

// Currency symbol mapping
export const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  EUR: '€',
  USD: '$',
  MXN: 'Mex$',
  ARS: 'ARS',
  CHF: 'Fr.',
  TRY: '₺',
  AED: 'د.إ',
  SAR: 'SR',
  BRL: 'R$',
  QAR: 'QAR',
};

// Vdr Setup Options - this is not currently used

export const jobRoleOptions = [
  { value: 'coach', label: 'Coach' },
  { value: 'manager', label: 'Manager' },
  { value: 'assistant-manager', label: 'Assistant Manager' },
  { value: 'director', label: 'Director' },
  { value: 'scout', label: 'Scout' },
  { value: 'other', label: 'Other' },
];

// Player Transfer Contract Options

export const playerRepresentationOptions = [
  { value: PlayerRepresentation.SelfRepresentation, label: 'Self-Representation' },
  { value: PlayerRepresentation.Agent, label: 'Agent' },
];

export const transferTypeOptions: RadioOption<string>[] = [
  { value: 'PERMANENT', label: 'Permanent' },
  { value: 'LOAN', label: 'Loan' },
];

export const instalmentOptions = [
  { value: '1', label: '1 month' },
  { value: '3', label: '3 months' },
  { value: '6', label: '6 months' },
];

// Player Contract Options

export const salaryOptions = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY', label: 'Yearly' },
];

export const contactDurationOptions = [
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3 years' },
  { value: '4', label: '4 years' },
  { value: '5', label: '5 years' },
  { value: '6', label: '6 years' },
  { value: '7', label: '7 years' },
];

//Financing Contract Options

export const financingTypeOptions = [
  { value: FinancingType.PlayerTransfer, label: 'Transfer Factoring' },
  { value: FinancingType.Payable, label: 'Payables Financing' },
  { value: FinancingType.SellOnFees, label: 'Sell-On Fees' },
  { value: FinancingType.TermLoan, label: 'Term Loan' },
  { value: FinancingType.BridgeLoan, label: 'Bridge Loan' },
  { value: FinancingType.MediaRights, label: 'TV/Media Rights' },
  { value: FinancingType.GeneralApplication, label: 'General Application' },
  { value: FinancingType.GeneralEnquiry, label: 'General Enquiry' },
];

export const allowedFinancingTypeOptions = [
  {
    value: FinancingType.BridgeLoan,
    label: 'Bridge Loan',
  },
  {
    value: FinancingType.GeneralApplication,
    label: 'General Application',
  },
  {
    value: FinancingType.GeneralEnquiry,
    label: 'General Enquiry',
  },
  {
    value: FinancingType.MediaRights,
    label: 'Media Rights',
  },
  {
    value: FinancingType.Payable,
    label: 'Payable',
  },
  {
    value: FinancingType.PlayerTransfer,
    label: 'Transfer Factoring',
  },
  {
    value: FinancingType.SellOnFees,
    label: 'Sell-On Fees',
  },
  {
    value: FinancingType.TermLoan,
    label: 'Term Loan',
  },
];

export const financierTypeOptions = [
  { value: FinancierType.AssetManager, label: 'Asset Manager' },
  { value: FinancierType.BankFinancialInstitution, label: 'Bank (Financial Institution)' },
  { value: FinancierType.FamilyOfficeHnwCapital, label: 'Family Office / HNW Capital' },
  { value: FinancierType.HedgeFund, label: 'Hedge Fund' },
  { value: FinancierType.PrivateCreditFund, label: 'Private Credit Fund' },
  { value: FinancierType.PrivateEquity, label: 'Private Equity' },
  { value: FinancierType.SovereignWealthFund, label: 'Sovereign Wealth Fund' },
  { value: FinancierType.SpecialSituation, label: 'Special Situation' },
];

export const contractOptionsFilter = [
  { value: 'All', label: 'All Statuses' },
  { value: ContractStatus.Accepted, label: 'Completed' },
  { value: ContractStatus.Rejected, label: 'Rejected' },
  { value: ContractStatus.InNegotiation, label: 'In Negotiation' },
  { value: ContractStatus.AwaitingResponse, label: 'Awaiting Acceptance' },
  { value: ContractStatus.Draft, label: 'Draft' },
];

export type ContractStatusOption = {
  value: ContractStatus | 'All';
  label: string;
};

export function getContractOptionsFilter(translate: any): ContractStatusOption[] {
  return [
    { value: 'All', label: translate('contractOptionsFilter.allStatuses') },
    { value: ContractStatus.Accepted, label: translate('contractOptionsFilter.completed') },
    { value: ContractStatus.Rejected, label: translate('contractOptionsFilter.rejected') },
    { value: ContractStatus.InNegotiation, label: translate('contractOptionsFilter.inNegotiation') },
    { value: ContractStatus.AwaitingResponse, label: translate('contractOptionsFilter.awaitingAcceptance') },
    { value: ContractStatus.Draft, label: translate('contractOptionsFilter.draft') },
  ];
}

export const structureOptions = [
  { label: 'Term Loan', value: 'termLoan' },
  { label: 'Revolving Credit Facility', value: 'revolvingCreditFacility' },
  { label: 'Bridge Loan', value: 'bridgeLoan' },
  { label: 'Syndicated Loan', value: 'syndicatedLoan' },
  { label: 'Receivable Purchase Agreement (RPA)', value: 'receivablePurchaseAgreement' },
];

//application top level filtering
export const applicationTypeOptions = [
  { value: ApplicationType.BridgeLoanFinancing, label: 'Bridge Loan' },
  { value: ApplicationType.MediaRightsFinancing, label: 'Media Rights Factoring' },
  { value: ApplicationType.TermLoanFinancing, label: 'Term Loan' },
  { value: ApplicationType.PayableFinancing, label: 'Payables' },
  { value: ApplicationType.SellOnFeesFinancing, label: 'Sell-On Fees' },
  { value: ApplicationType.PlayerTransferFinancing, label: 'Transfer Factoring' },
  { value: ApplicationType.GeneralApplicationFinancing, label: 'General Application' },
  { value: ApplicationType.GeneralEnquiryFinancing, label: 'General Enquiry' },
];

export const applicationStatusOptions = [
  { value: ApplicationStatus.Approved, label: 'Matching With funders' },
  { value: ApplicationStatus.Concluded, label: 'Concluded' },
  { value: ApplicationStatus.Draft, label: 'Draft' },
  // { value: ApplicationStatus.Pending, label: 'Pending' }
  // { value: ApplicationStatus.Rejected, label: 'Rejected' },
  // { value: ApplicationStatus.Withdrawn, label: 'Withdrawn' }
];

// Financier side - application requests
export const applicationRequestStatusOptions = [
  { value: ApplicationRequestStatus.Accepted, label: 'Approved' },
  { value: ApplicationRequestStatus.Pending, label: 'Pending' },
  { value: ApplicationRequestStatus.Rejected, label: 'Rejected' },
];

export const financingContractStatusOptions = [
  { value: ContractStatus.Accepted, label: 'Funded' },
  { value: ContractStatus.Rejected, label: 'Lost' },
  { value: ContractStatus.InNegotiation, label: 'Under Negotiation' },
  { value: ContractStatus.AwaitingResponse, label: 'Awaiting Response' },
  { value: ContractStatus.Draft, label: 'Draft' },
];

export const dealTermOptions = [
  { label: 'Up to 1 year', value: 'UP_TO_1_YEAR' },
  { label: 'Up to 2 years', value: 'UP_TO_2_YEARS' },
  { label: 'Up to 3 years', value: 'UP_TO_3_YEARS' },
  { label: 'Up to 4 years', value: 'UP_TO_4_YEARS' },
  { label: 'Up to 5 years', value: 'UP_TO_5_YEARS' },
];

export type selectOption = {
  label: string;
  value: string;
};

export function getDealTermOptions(translate: any): selectOption[] {
  return [
    { label: translate('dealTerm.dealTermOptions.1year'), value: 'UP_TO_1_YEAR' },
    { label: translate('dealTerm.dealTermOptions.2years'), value: 'UP_TO_2_YEARS' },
    { label: translate('dealTerm.dealTermOptions.3years'), value: 'UP_TO_3_YEARS' },
    { label: translate('dealTerm.dealTermOptions.4years'), value: 'UP_TO_4_YEARS' },
    { label: translate('dealTerm.dealTermOptions.5years'), value: 'UP_TO_5_YEARS' },
  ];
}

export function getLoanTermOptionsLong(translate: any, isBridgeLoan?: boolean): selectOption[] {
  return [
    { label: translate('loanTerm.long.1month'), value: '1_MONTH' },
    { label: translate('loanTerm.long.2months'), value: '2_MONTHS' },
    { label: translate('loanTerm.long.3months'), value: '3_MONTHS' },
    { label: translate('loanTerm.long.4months'), value: '4_MONTHS' },
    { label: translate('loanTerm.long.5months'), value: '5_MONTHS' },
    { label: translate('loanTerm.long.6months'), value: '6_MONTHS' },
    { label: translate('loanTerm.long.7months'), value: '7_MONTHS' },
    { label: translate('loanTerm.long.8months'), value: '8_MONTHS' },
    { label: translate('loanTerm.long.9months'), value: '9_MONTHS' },
    { label: translate('loanTerm.long.10months'), value: '10_MONTHS' },
    { label: translate('loanTerm.long.11months'), value: '11_MONTHS' },
    { label: translate('loanTerm.long.12months'), value: '12_MONTHS' },
    ...(isBridgeLoan ? [{ label: translate('loanTerm.long.12months+'), value: '12_MONTHS_+' }] : []),
  ];
}

export function getLoanTermOptionsShort(translate: any): selectOption[] {
  return [
    { label: translate('loanTerm.short.upTo1Year'), value: 'UP_TO_1_YEAR' },
    { label: translate('loanTerm.short.upTo2Years'), value: 'UP_TO_2_YEARS' },
    { label: translate('loanTerm.short.upTo3Years'), value: 'UP_TO_3_YEARS' },
    { label: translate('loanTerm.short.upTo4Years'), value: 'UP_TO_4_YEARS' },
    { label: translate('loanTerm.short.upTo5Years'), value: 'UP_TO_5_YEARS' },
  ];
}

export const interestPaymentPeriodOptions = [
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Quarterly', value: 'QUARTERLY' },
  { label: 'Semi-Annually', value: 'SEMI_ANNUALLY' },
  { label: 'Annually', value: 'ANNUALLY' },
];

export function getInterestPaymentPeriodOptions(translate: any): selectOption[] {
  return [
    { label: translate('interestPaymentPeriodOptions.weekly'), value: 'WEEKLY' },
    { label: translate('interestPaymentPeriodOptions.monthly'), value: 'MONTHLY' },
    { label: translate('interestPaymentPeriodOptions.quarterly'), value: 'QUARTERLY' },
    { label: translate('interestPaymentPeriodOptions.semi-anually'), value: 'SEMI_ANNUALLY' },
    { label: translate('interestPaymentPeriodOptions.anually'), value: 'ANNUALLY' },
  ];
}

export function getDebtTypeOptions(translate: any): { label: string; value: string }[] {
  return [
    { label: translate('existingDebtArrangements.debtTypeOptions.termLoan'), value: 'TERM_LOAN' },
    { label: translate('existingDebtArrangements.debtTypeOptions.bridgeLoan'), value: 'BRIDGE_LOAN' },
    {
      label: translate('existingDebtArrangements.debtTypeOptions.revolvingCreditFacility'),
      value: 'REVOLVING_CREDIT_FACILITY',
    },
    { label: translate('existingDebtArrangements.debtTypeOptions.recourseFactoring'), value: 'RECOURSE_FACTORING' },
    { label: translate('existingDebtArrangements.debtTypeOptions.payables'), value: 'PAYABLES' },
    {
      label: translate('existingDebtArrangements.debtTypeOptions.acquisitionFinance'),
      value: 'ACQUISITION_FIN_OR_LEVERAGED_LOAN',
    },
    { label: translate('existingDebtArrangements.debtTypeOptions.other'), value: 'OTHER' },
  ];
}

export const dealTypeOptions = [
  { label: 'Revolving Credit Facility (RCF)', value: 'REVOLVING_CREDIT_FACILITY' },
  { label: 'Acquisition Finance', value: 'ACQUISITION_FINANCE' },
  { label: 'MCO Refinancing', value: 'MCO_REFINANCING' },
  { label: 'Leveraged Loan', value: 'LEVERAGED_LOAN' },
  { label: 'Asset-Backed Loan (ABL)', value: 'ASSET_BACKED_LOAN' },
  { label: 'Infrastructure Financing (Stadium or Training Facilities)', value: 'INFRASTRUCTURE_FINANCING' },
  // { label: 'Training Facility', value: 'TRAINING_FACILITY' },
  { label: 'Ticketing', value: 'TICKETING' },
  { label: 'Sponsorship Receivables', value: 'SPONSORSHIP_RECEIVABLES' },
  { label: 'Other', value: 'OTHER' },
];

export function getDealTypeOptions(translate: any): selectOption[] {
  return [
    { label: translate('dealTypeOptions.rcf'), value: 'REVOLVING_CREDIT_FACILITY' },
    { label: translate('dealTypeOptions.aquisitionFinance'), value: 'ACQUISITION_FINANCE' },
    { label: translate('dealTypeOptions.mcoRefinancing'), value: 'MCO_REFINANCING' },
    { label: translate('dealTypeOptions.leveragedLoan'), value: 'LEVERAGED_LOAN' },
    { label: translate('dealTypeOptions.abl'), value: 'ASSET_BACKED_LOAN' },
    { label: translate('dealTypeOptions.infrastructureFinancing'), value: 'INFRASTRUCTURE_FINANCING' },
    // { label: 'Training Facility', value: 'TRAINING_FACILITY' },
    { label: translate('dealTypeOptions.ticketing'), value: 'TICKETING' },
    { label: translate('dealTypeOptions.sponsorshipReceivables'), value: 'SPONSORSHIP_RECEIVABLES' },
    { label: translate('dealTypeOptions.other'), value: 'OTHER' },
  ];
}

export const rejectionReasonOptionsFinancier = [
  { label: 'Risk Profile', value: 'RISK_PROFILE' },
  { label: 'Repayment Profile', value: 'REPAYMENT_PROFILE' },
  { label: 'Deal Maturity', value: 'DEAL_MATURITY' },
  { label: 'Financing Volume', value: 'FINANCING_VOLUME' },
  { label: 'Obligor Name Limit', value: 'OBLIGOR_NAME_LIMIT' },
  { label: 'Expected Funding Date', value: 'EXPECTED_FUNDING_DATE' },
  { label: 'Other', value: 'OTHER' },
];

export const rejectionReasonOptionsClub = [
  { label: 'Too high interest', value: 'TOO_HIGH_INTEREST' },
  { label: 'Expected Funding Date', value: 'EXPECTED_FUNDING_DATE' },
  { label: 'No longer wish to finance', value: 'NO_LONGER_WISH_TO_FINANCE' },
  { label: 'Too high fees', value: 'TOO_HIGH_FEES' },
];

export const contractInterestPaymentPeriodOptions = [
  ...interestPaymentPeriodOptions.map((option) => ({ label: option.label, value: option.value })),
  { label: 'Payment in Kind (PIK)', value: 'PAYMENT_IN_KIND' },
];

export function getContractInterestPaymentPeriodOptions(translate: any): { label: string; value: string }[] {
  return [
    { label: translate('interestPaymentPeriod.weekly'), value: 'WEEKLY' },
    { label: translate('interestPaymentPeriod.monthly'), value: 'MONTHLY' },
    { label: translate('interestPaymentPeriod.quarterly'), value: 'QUARTERLY' },
    { label: translate('interestPaymentPeriod.semiAnnually'), value: 'SEMI_ANNUALLY' },
    { label: translate('interestPaymentPeriod.annually'), value: 'ANNUALLY' },
    { label: translate('interestPaymentPeriod.paymentInKind'), value: 'PAYMENT_IN_KIND' },
  ];
}

export type ExpandableCategory = {
  name: string;
  expandable: boolean;
  options: string[];
};

export const securityExpandableOptionsShort: ExpandableCategory[] = [
  {
    name: 'Player-Related Assets',
    expandable: true,
    options: [
      'Transfer Receivables (Non Recourse)',
      'Transfer Receivables (With Recourse)',
      'Sell-on Clauses / Future Transfer Rights',
    ],
  },
  {
    name: 'Broadcasting & Media',
    expandable: true,
    options: [
      'TV/Media Rights (Domestic & International)',
      'Digital & Streaming Rights',
      'Performance-Related TV Bonuses',
    ],
  },
];

export const securityExpandableOptionsLong: ExpandableCategory[] = [
  {
    name: 'Player-Related Assets',
    expandable: true,
    options: [
      'Transfer Receivables (Non Recourse)',
      'Transfer Receivables (With Recourse)',
      'Sell-on Clauses / Future Transfer Rights',
      'Training Compensation / Solidarity Payments',
      'Player Image Rights Income',
      'Player Loan Fee Income',
    ],
  },
  {
    name: 'Commercial & Matchday Income',
    expandable: true,
    options: [
      'Commercial Income (Sponsorships, Partnerships, Naming Rights)',
      'Advertising Income',
      'Merchandise Income (Retail, Online, Licensing)',
      'Ticketing Income (Matchday + Season Tickets)',
      'Hospitality & VIP Income',
      'Non-Matchday Events Income',
    ],
  },
  {
    name: 'Broadcasting & Media',
    expandable: true,
    options: [
      'TV/Media Rights (Domestic & International)',
      'Digital & Streaming Rights',
      'Performance-Related TV Bonuses',
    ],
  },
  {
    name: 'Fixed Assets (Secured)',
    expandable: true,
    options: [
      'Stadium',
      'Training Ground / Academy Facilities',
      'Club-Owned Real Estate / Property',
      'Future Stadium Income (from redevelopment.)',
    ],
  },
  {
    name: 'All Asset Debenture',
    expandable: true,
    options: [
      'Cash Flow / Revenue Assignment',
      'Parent or Ownership Guarantees',
      'Insurance Proceeds / Key Man Policies (e.g., player insurance)',
      'Escrowed Accounts / Controlled Revenue Accounts',
    ],
  },
  {
    name: 'League & Competition Distributions',
    expandable: true,
    options: [
      'League Central Distributions',
      'UEFA / FIFA Prize Money',
      'Parachute Payments',
      'Development Grants / Solidarity Mechanisms',
    ],
  },
  {
    name: 'Other / Emerging Assets',
    expandable: true,
    options: [
      'Future Equity Pledges',
      'Collective Bargaining Revenue',
      'Fan Token / NFT / Web3 Income',
      'Branding Partnerships',
      'Academy Player Sale Pipeline',
      'Deferred Receivables',
      'Unsecured / General',
      'Unsecured',
      'Tax Refunds or Credits',
    ],
  },
];

export function getSecurityExpandableOptionsLong(translate: any): ExpandableCategory[] {
  return [
    {
      name: translate('securityExpandableOptionsLong.playerRelatedAssets.label'),
      expandable: true,
      options: [
        translate('securityExpandableOptionsLong.playerRelatedAssets.options.transferReceivablesNonRecourse'),
        translate('securityExpandableOptionsLong.playerRelatedAssets.options.transferReceivablesWithRecourse'),
        translate('securityExpandableOptionsLong.playerRelatedAssets.options.sellOnClauses'),
        translate('securityExpandableOptionsLong.playerRelatedAssets.options.trainingCompensation'),
        translate('securityExpandableOptionsLong.playerRelatedAssets.options.playerImageRights'),
        translate('securityExpandableOptionsLong.playerRelatedAssets.options.playerLoanFee'),
      ],
    },
    {
      name: translate('securityExpandableOptionsLong.commercialAndMatchdayIncome.label'),
      expandable: true,
      options: [
        translate('securityExpandableOptionsLong.commercialAndMatchdayIncome.options.commercialIncome'),
        translate('securityExpandableOptionsLong.commercialAndMatchdayIncome.options.advertisingIncome'),
        translate('securityExpandableOptionsLong.commercialAndMatchdayIncome.options.merchandiseIncome'),
        translate('securityExpandableOptionsLong.commercialAndMatchdayIncome.options.ticketingIncome'),
        translate('securityExpandableOptionsLong.commercialAndMatchdayIncome.options.hospitalityIncome'),
        translate('securityExpandableOptionsLong.commercialAndMatchdayIncome.options.nonMatchdayEvents'),
      ],
    },
    {
      name: translate('securityExpandableOptionsLong.broadcastingAndMedia.label'),
      expandable: true,
      options: [
        translate('securityExpandableOptionsLong.broadcastingAndMedia.options.tvMediaRights'),
        translate('securityExpandableOptionsLong.broadcastingAndMedia.options.digitalStreamingRights'),
        translate('securityExpandableOptionsLong.broadcastingAndMedia.options.performanceRelatedTVBonuses'),
      ],
    },
    {
      name: translate('securityExpandableOptionsLong.fixedAssetsSecured.label'),
      expandable: true,
      options: [
        translate('securityExpandableOptionsLong.fixedAssetsSecured.options.stadium'),
        translate('securityExpandableOptionsLong.fixedAssetsSecured.options.trainingGround'),
        translate('securityExpandableOptionsLong.fixedAssetsSecured.options.clubOwnedProperty'),
        translate('securityExpandableOptionsLong.fixedAssetsSecured.options.futureStadiumIncome'),
      ],
    },
    {
      name: translate('securityExpandableOptionsLong.allAssetDebenture.label'),
      expandable: true,
      options: [
        translate('securityExpandableOptionsLong.allAssetDebenture.options.cashFlowRevenueAssignment'),
        translate('securityExpandableOptionsLong.allAssetDebenture.options.personalGuarantees'),
        translate('securityExpandableOptionsLong.allAssetDebenture.options.insuranceProceeds'),
        translate('securityExpandableOptionsLong.allAssetDebenture.options.escrowedAccounts'),
      ],
    },
    {
      name: translate('securityExpandableOptionsLong.leagueAndCompetitionDistributions.label'),
      expandable: true,
      options: [
        translate('securityExpandableOptionsLong.leagueAndCompetitionDistributions.options.leagueCentralDistributions'),
        translate('securityExpandableOptionsLong.leagueAndCompetitionDistributions.options.uefaFifaPrizeMoney'),
        translate('securityExpandableOptionsLong.leagueAndCompetitionDistributions.options.parachutePayments'),
        translate('securityExpandableOptionsLong.leagueAndCompetitionDistributions.options.developmentGrants'),
      ],
    },
    {
      name: translate('securityExpandableOptionsLong.otherEmergingAssets.label'),
      expandable: true,
      options: [
        translate('securityExpandableOptionsLong.otherEmergingAssets.options.futureEquityPledges'),
        translate('securityExpandableOptionsLong.otherEmergingAssets.options.collectiveBargainingRevenue'),
        translate('securityExpandableOptionsLong.otherEmergingAssets.options.fanTokenIncome'),
        translate('securityExpandableOptionsLong.otherEmergingAssets.options.brandingPartnerships'),
        translate('securityExpandableOptionsLong.otherEmergingAssets.options.academyPlayerPipeline'),
        translate('securityExpandableOptionsLong.otherEmergingAssets.options.deferredReceivables'),
        translate('securityExpandableOptionsLong.otherEmergingAssets.options.unsecuredGeneral'),
        translate('securityExpandableOptionsLong.otherEmergingAssets.options.unsecured'),
        translate('securityExpandableOptionsLong.otherEmergingAssets.options.taxRefunds'),
      ],
    },
  ];
}

export function getSourceOfRepaymentExpandableOptionsLong(translate: any): ExpandableCategory[] {
  return [
    {
      name: translate('sourceOfRepaymentExpandableOptions.options.playerRelatedAssets.label'),
      expandable: true,
      options: [
        translate('sourceOfRepaymentExpandableOptions.options.playerRelatedAssets.options.transferReceivables'),
        translate(
          'sourceOfRepaymentExpandableOptions.options.playerRelatedAssets.options.sellOnClausesFutureTransferRights',
        ),
        translate(
          'sourceOfRepaymentExpandableOptions.options.playerRelatedAssets.options.trainingCompensationSolidarityPayments',
        ),
        translate('sourceOfRepaymentExpandableOptions.options.playerRelatedAssets.options.playerImageRightsIncome'),
        translate('sourceOfRepaymentExpandableOptions.options.playerRelatedAssets.options.playerLoanFeeIncome'),
      ],
    },
    {
      name: translate('sourceOfRepaymentExpandableOptions.options.commercialAndMatchdayIncome.label'),
      expandable: true,
      options: [
        translate('sourceOfRepaymentExpandableOptions.options.commercialAndMatchdayIncome.options.commercialIncome'),
        translate('sourceOfRepaymentExpandableOptions.options.commercialAndMatchdayIncome.options.advertisingIncome'),
        translate('sourceOfRepaymentExpandableOptions.options.commercialAndMatchdayIncome.options.merchandiseIncome'),
        translate('sourceOfRepaymentExpandableOptions.options.commercialAndMatchdayIncome.options.ticketingIncome'),
        translate(
          'sourceOfRepaymentExpandableOptions.options.commercialAndMatchdayIncome.options.hospitalityVipIncome',
        ),
        translate(
          'sourceOfRepaymentExpandableOptions.options.commercialAndMatchdayIncome.options.nonMatchdayEventsIncome',
        ),
      ],
    },
    {
      name: translate('sourceOfRepaymentExpandableOptions.options.broadcastingAndMedia.label'),
      expandable: true,
      options: [
        translate(
          'sourceOfRepaymentExpandableOptions.options.broadcastingAndMedia.options.tvMediaRightsDomesticInternational',
        ),
        translate('sourceOfRepaymentExpandableOptions.options.broadcastingAndMedia.options.digitalStreamingRights'),
        translate(
          'sourceOfRepaymentExpandableOptions.options.broadcastingAndMedia.options.performanceRelatedTvBonuses',
        ),
      ],
    },
    {
      name: translate('sourceOfRepaymentExpandableOptions.options.fixedAssetsSecured.label'),
      expandable: true,
      options: [
        translate('sourceOfRepaymentExpandableOptions.options.fixedAssetsSecured.options.stadiumSale'),
        translate(
          'sourceOfRepaymentExpandableOptions.options.fixedAssetsSecured.options.trainingGroundAcademyFacilitiesSale',
        ),
        translate(
          'sourceOfRepaymentExpandableOptions.options.fixedAssetsSecured.options.clubOwnedRealEstatePropertySale',
        ),
      ],
    },
    {
      name: translate('sourceOfRepaymentExpandableOptions.options.allAssetDebenture.label'),
      expandable: true,
      options: [
        translate('sourceOfRepaymentExpandableOptions.options.allAssetDebenture.options.cashFlowRevenueAssignment'),
        translate('sourceOfRepaymentExpandableOptions.options.allAssetDebenture.options.parentOrOwnershipGuarantees'),
        translate(
          'sourceOfRepaymentExpandableOptions.options.allAssetDebenture.options.insuranceProceedsKeyManPolicies',
        ),
        translate(
          'sourceOfRepaymentExpandableOptions.options.allAssetDebenture.options.escrowedAccountsControlledRevenueAccounts',
        ),
      ],
    },
    {
      name: translate('sourceOfRepaymentExpandableOptions.options.leagueCompetitionDistributions.label'),
      expandable: true,
      options: [
        translate(
          'sourceOfRepaymentExpandableOptions.options.leagueCompetitionDistributions.options.leagueCentralDistributions',
        ),
        translate(
          'sourceOfRepaymentExpandableOptions.options.leagueCompetitionDistributions.options.uefaFifaPrizeMoney',
        ),
        translate(
          'sourceOfRepaymentExpandableOptions.options.leagueCompetitionDistributions.options.parachutePayments',
        ),
        translate(
          'sourceOfRepaymentExpandableOptions.options.leagueCompetitionDistributions.options.developmentGrantsSolidarityMechanisms',
        ),
      ],
    },
    {
      name: translate('sourceOfRepaymentExpandableOptions.options.otherEmergingAssets.label'),
      expandable: true,
      options: [
        translate('sourceOfRepaymentExpandableOptions.options.otherEmergingAssets.options.futureEquityPledges'),
        translate('sourceOfRepaymentExpandableOptions.options.otherEmergingAssets.options.collectiveBargainingRevenue'),
        translate('sourceOfRepaymentExpandableOptions.options.otherEmergingAssets.options.fanTokenNftWeb3Income'),
        translate('sourceOfRepaymentExpandableOptions.options.otherEmergingAssets.options.brandingPartnerships'),
        translate('sourceOfRepaymentExpandableOptions.options.otherEmergingAssets.options.academyPlayerSalePipeline'),
        translate('sourceOfRepaymentExpandableOptions.options.otherEmergingAssets.options.deferredReceivables'),
        translate('sourceOfRepaymentExpandableOptions.options.otherEmergingAssets.options.taxRefundsCredits'),
      ],
    },
  ];
}

export const securityBasisShort = [
  { label: 'None', value: 'NONE' },
  { label: 'Secured', value: 'SECURED' },
  { label: 'Unsecured', value: 'UNSECURED' },
  { label: 'Assigned Receivables', value: 'ASSIGNED_RECEIVABLES' },
  { label: 'Revenue Assignment', value: 'REVENUE_ASSIGNMENT' },
];

export const securityBasisLong = [
  { label: 'None', value: 'NONE' },
  { label: 'Secured', value: 'SECURED' },
  { label: 'Unsecured', value: 'UNSECURED' },
  { label: 'Assigned Receivables', value: 'ASSIGNED_RECEIVABLES' },
  { label: 'Assigned Payables', value: 'ASSIGNED_PAYABLES' },
  { label: 'Strongly Secured', value: 'STRONGLY_SECURED' },
  { label: 'Asset Pool', value: 'ASSET_POOL' },
  { label: 'Shared Security', value: 'SHARED_SECURITY' },
  { label: 'Equity Option', value: 'EQUITY_OPTION' },
  { label: 'Asset Title', value: 'ASSET_TITLE' },
  { label: 'Cash Collateral', value: 'CASH_COLLATERAL' },
  { label: 'Bank Guarantee', value: 'BANK_GUARANTEE' },
  { label: 'Revenue Assignment', value: 'REVENUE_ASSIGNMENT' },
  { label: 'Assigned Revenue', value: 'ASSIGNED_REVENUE' },
  { label: 'Limited Security', value: 'LIMITED_SECURITY' },
];

export const fundingStructureOptionsShort = [
  { label: 'Non-Recourse Factoring (RPA)', value: 'NON_RECOURSE_FACTORING_RPA' },
  { label: 'Recourse Factoring (RPA or Loan)', value: 'RECOURSE_FACTORING_RPA_OR_LOAN' },
  { label: 'Payables Financing (Reverse Factoring)', value: 'PAYABLES_FINANCING_REVERSE_FACTORING' },
];

export const fundingStructureOptionsLong: OptionGroup[] = [
  {
    label: 'Loan Facilities',
    options: [
      { label: 'Term Loan', value: 'Term Loan' },
      { label: 'Bridge Loan', value: 'Bridge Loan' },
      { label: 'Revolving Credit Facility (RCF)', value: 'Revolving Credit Facility (RCF)' },
      { label: 'Syndicated Loan', value: 'Syndicated Loan' },
      { label: 'Asset-Backed Loan (ABL)', value: 'Asset-Backed Loan (ABL)' },
      { label: 'Acquisition Finance / Leveraged Loan', value: 'Acquisition Finance / Leveraged Loan' },
    ],
  },
  {
    label: 'Factoring & Receivable-Based',
    options: [
      { label: 'Recourse Factoring', value: 'Recourse Factoring' },
      { label: 'Non-Recourse Factoring', value: 'Non-Recourse Factoring' },
      { label: 'Receivables Purchase Agreement (RPA)', value: 'Receivables Purchase Agreement (RPA)' },
      { label: 'Payables Financing (Reverse Factoring)', value: 'Payables Financing (Reverse Factoring)' },
      { label: 'Invoice Discounting', value: 'Invoice Discounting' },
    ],
  },
  {
    label: 'Structured & Capital Market',
    options: [
      { label: 'Securitisation (ABS)', value: 'Securitisation (ABS)' },
      { label: 'Note Issuance / Bond', value: 'Note Issuance / Bond' },
      { label: 'Private Placement', value: 'Private Placement' },
      { label: 'Loan Participation / Sub-Participation', value: 'Loan Participation / Sub-Participation' },
      { label: 'Credit-Linked Note (CLN)', value: 'Credit-Linked Note (CLN)' },
    ],
  },
  {
    label: 'Hybrid / Equity-Linked',
    options: [
      { label: 'Convertible Loan Note (CLN)', value: 'Convertible Loan Note (CLN)' },
      { label: 'SAFE / Convertible Instrument', value: 'SAFE / Convertible Instrument' },
      { label: 'Preferred Equity / Quasi-Debt', value: 'Preferred Equity / Quasi-Debt' },
    ],
  },
  {
    label: 'Project / Asset Finance',
    options: [
      { label: 'Project Finance Loan', value: 'Project Finance Loan' },
      { label: 'Sale & Leaseback', value: 'Sale & Leaseback' },
      { label: 'Operating Lease / Hire Purchase', value: 'Operating Lease / Hire Purchase' },
    ],
  },
  {
    label: 'Short-Term / Cash Management',
    options: [
      { label: 'Overdraft Facility', value: 'Overdraft Facility' },
      { label: 'Cash Collateralised Loan', value: 'Cash Collateralised Loan' },
    ],
  },
  {
    label: 'Special Purpose Structures',
    options: [
      { label: 'SPV Lending / Assignment Vehicle', value: 'SPV Lending / Assignment Vehicle' },
      { label: 'Co-Lending or Club Deal', value: 'Co-Lending or Club Deal' },
      { label: 'Back-Leverage Facility', value: 'Back-Leverage Facility' },
      { label: 'Participation Agreement', value: 'Participation Agreement' },
    ],
  },
  {
    label: 'Trade / Supplier Finance',
    options: [
      { label: 'Letter of Credit (LC)', value: 'Letter of Credit (LC)' },
      { label: 'Bank Guarantee / Performance Bond', value: 'Bank Guarantee / Performance Bond' },
    ],
  },
  {
    label: 'Other / Alternative',
    options: [
      { label: 'Revenue Participation Agreement', value: 'Revenue Participation Agreement' },
      { label: 'Royalty Financing', value: 'Royalty Financing' },
      { label: 'Debt Fund Facility Agreement', value: 'Debt Fund Facility Agreement' },
      { label: 'Crowdfunded / Peer Financing', value: 'Crowdfunded / Peer Financing' },
    ],
  },
];

export const recourseOptions = [
  { label: 'With Recourse', value: 'WITH_RECOURSE' },
  { label: 'Non-Recourse', value: 'NON_RECOURSE' },
  { label: 'With Recourse until Agreed Date or conversion', value: 'WITH_RECOURSE_UNTIL_AGREED_DATE_OR_CONVERSION' },
  { label: 'N/A', value: 'NOT_APPLICABLE' },
];

export function getRecourseOptions(translate: any): selectOption[] {
  return [
    { label: translate('recourse.withRecourse'), value: 'WITH_RECOURSE' },
    { label: translate('recourse.nonRecourse'), value: 'NON_RECOURSE' },
    { label: translate('recourse.withRecourseUntilAgreed'), value: 'WITH_RECOURSE_UNTIL_AGREED_DATE_OR_CONVERSION' },
    { label: translate('recourse.notApplicable'), value: 'NOT_APPLICABLE' },
  ];
}

export const tenorOptionsMonths = [
  { label: '1 month', value: '1_MONTH' },
  { label: '2 months', value: '2_MONTHS' },
  { label: '3 months', value: '3_MONTHS' },
  { label: '4 months', value: '4_MONTHS' },
  { label: '5 months', value: '5_MONTHS' },
  { label: '6 months', value: '6_MONTHS' },
  { label: '7 months', value: '7_MONTHS' },
  { label: '8 months', value: '8_MONTHS' },
  { label: '9 months', value: '9_MONTHS' },
  { label: '10 months', value: '10_MONTHS' },
  { label: '11 months', value: '11_MONTHS' },
  { label: '12 months', value: '12_MONTHS' },
];

export const tenorOptionsYears = [
  { label: 'Up to 1 year', value: 'UP_TO_1_YEAR' },
  { label: 'Up to 2 years', value: 'UP_TO_2_YEARS' },
  { label: 'Up to 3 years', value: 'UP_TO_3_YEARS' },
  { label: 'Up to 4 years', value: 'UP_TO_4_YEARS' },
  { label: 'Up to 5 years', value: 'UP_TO_5_YEARS' },
  { label: 'Up to 6 years', value: 'UP_TO_6_YEARS' },
  { label: 'Up to 7 years', value: 'UP_TO_7_YEARS' },
  { label: 'Up to 8 years', value: 'UP_TO_8_YEARS' },
  { label: 'Up to 9 years', value: 'UP_TO_9_YEARS' },
  { label: 'Up to 10 years', value: 'UP_TO_10_YEARS' },
  { label: 'Up to 11 years', value: 'UP_TO_11_YEARS' },
  { label: 'Up to 12 years', value: 'UP_TO_12_YEARS' },
  { label: 'Up to 13 years', value: 'UP_TO_13_YEARS' },
  { label: 'Up to 14 years', value: 'UP_TO_14_YEARS' },
  { label: 'Up to 15 years', value: 'UP_TO_15_YEARS' },
  { label: 'Up to 16 years', value: 'UP_TO_16_YEARS' },
  { label: 'Up to 17 years', value: 'UP_TO_17_YEARS' },
  { label: 'Up to 18 years', value: 'UP_TO_18_YEARS' },
  { label: 'Up to 19 years', value: 'UP_TO_19_YEARS' },
  { label: 'Up to 20 years', value: 'UP_TO_20_YEARS' },
  { label: 'Up to 21 years', value: 'UP_TO_21_YEARS' },
  { label: 'Up to 22 years', value: 'UP_TO_22_YEARS' },
  { label: 'Up to 23 years', value: 'UP_TO_23_YEARS' },
  { label: 'Up to 24 years', value: 'UP_TO_24_YEARS' },
  { label: 'Up to 25 years', value: 'UP_TO_25_YEARS' },
  { label: 'Up to 26 years', value: 'UP_TO_26_YEARS' },
  { label: 'Up to 27 years', value: 'UP_TO_27_YEARS' },
  { label: 'Up to 28 years', value: 'UP_TO_28_YEARS' },
  { label: 'Up to 29 years', value: 'UP_TO_29_YEARS' },
  { label: 'Up to 30 years', value: 'UP_TO_30_YEARS' },
];

export const creditApprovalStatusOptions = [
  { label: 'Conditional Approval', value: 'CONDITIONAL_APPROVAL' },
  { label: 'Full Approved', value: 'FULL_APPROVED' },
  { label: 'Indicative', value: 'INDICATIVE' },
];

export function getCreditApprovalStatusOptions(translate: any): { label: string; value: string }[] {
  return [
    { label: translate('creditApprovalStatus.conditionalApproval'), value: 'CONDITIONAL_APPROVAL' },
    { label: translate('creditApprovalStatus.fullApproved'), value: 'FULL_APPROVED' },
    { label: translate('creditApprovalStatus.indicative'), value: 'INDICATIVE' },
  ];
}

export function getListOfAssetsOptions(translate: any): selectOption[] {
  return [
    { label: translate('listOfAssetsOptions.stadium&Facilities'), value: 'Stadium & Facilities' },
    {
      label: translate('listOfAssetsOptions.trainingGround/AcademyFacilities'),
      value: 'Training Ground / Academy Facilities',
    },
    { label: translate('listOfAssetsOptions.communityFacilities'), value: 'Community Facilities' },
    {
      label: translate('listOfAssetsOptions.club-OwnedRealEstate/Property'),
      value: 'Club-Owned Real Estate / Property',
    },
    { label: translate('listOfAssetsOptions.equipment'), value: 'Equipment' },
  ];
}

export const additionalDocumentsExpandableOptions = [
  {
    name: 'Financial Details',
    expandable: true,
    options: [
      'P&L',
      'Balance Sheet',
      'Interim Accounts',
      'Historical Audited Accounts',
      'Management accounts (YTD)',
      'Current debt schedule',
      'Bank statements',
      'Cashflows',
      'List of Transfer Receivables',
      'List of Transfer Payables',
      'List of Assets',
      'Contracted Sponsorship',
      'Ticketing',
    ],
  },
  {
    name: 'Stadium Project Details',
    expandable: true,
    options: [
      'Overview of project scope, design, and capacity',
      'Construction timeline and key milestones',
      'Total budget breakdown (land, construction, fit-out, contingencies)',
      'List of contractors, architects, and project managers',
      'Status of permits and planning approvals',
      'ESG and sustainability initiatives',
    ],
  },
  {
    name: 'Future Financial Projections (5-10+ years)',
    expandable: true,
    options: [
      'Revenue and expense projections with assumptions (matchday, sponsorship, retail, etc.)',
      'Expected uplift from new stadium',
      'Cash flow forecasts',
      'Debt servicing capacity and repayment plan',
      'Sensitivity and break-even analysis',
    ],
  },
  {
    name: 'Debt and Financing Plans',
    expandable: true,
    options: [
      'Proposed financing structure (equity vs. debt)',
      'Sources of funds (loans, grants, sponsor contributions, etc.)',
      'Key terms of financing (interest, duration, repayment)',
      'Security/collateral offered (ticketing, naming rights, etc.)',
      'Club credit rating (if applicable)',
    ],
  },
  {
    name: 'Revenue Generation Plans',
    expandable: true,
    options: [
      'Ticketing and pricing strategy',
      'Sponsorship and naming rights (existing or pipeline)',
      'Hospitality, retail, concessions, events',
      'Media and broadcasting impact',
    ],
  },
  {
    name: 'Operational Considerations',
    expandable: true,
    options: [
      'Stadium operations model',
      'Maintenance cost estimates',
      'Fan experience and accessibility plans',
      'Safety, security, and technology strategy',
    ],
  },
  {
    name: 'Legal and Compliance',
    expandable: true,
    options: [
      'Ongoing/pending legal matters',
      'Key contracts (sponsorships, suppliers, media rights)',
      'Insurance coverage',
    ],
  },
  {
    name: 'Stakeholder Engagement',
    expandable: true,
    options: [
      'Fan and community engagement initiatives',
      'Government or municipal support',
      'Strategic partners or potential co-investors',
    ],
  },
  {
    name: 'ESG Considerations',
    expandable: true,
    options: ['Environmental impact plan', 'Community and social programs', 'Governance and transparency policies'],
  },
  {
    name: 'Rating Agency Preparation',
    expandable: true,
    options: [
      'Documentation aligned to credit rating criteria',
      'Financial stress testing',
      'Liquidity and capital adequacy analysis',
      'Benchmark comparisons',
    ],
  },
];

export const conditionsPrecedentOptions: OptionGroup[] = [
  {
    label: 'Transaction Documents',
    options: [
      { value: 'Submission of Signed RPA', label: 'Submission of Signed RPA' },
      {
        value: 'Submission of Signed NoA (Notice of Assignment)',
        label: 'Submission of Signed NoA (Notice of Assignment)',
      },
      { value: 'Proof of Delivery of NoA to the Obligor', label: 'Proof of Delivery of NoA to the Obligor' },
      { value: 'Submission of NoA Signed by Obligor', label: 'Submission of NoA Signed by Obligor' },
      {
        value: 'Submission of Deed of Charge / Security Agreement',
        label: 'Submission of Deed of Charge / Security Agreement',
      },
      {
        value: 'Signed Borrower Undertaking / Representation Letter',
        label: 'Signed Borrower Undertaking / Representation Letter',
      },
      {
        value: 'Signed Intercreditor / Subordination Agreement',
        label: 'Signed Intercreditor / Subordination Agreement',
      },
    ],
  },
  {
    label: 'Financial Evidence',
    options: [
      { value: 'Proof of Payment of Prior Instalments', label: 'Proof of Payment of Prior Instalments' },
      { value: 'Copy of Invoice Sent to Obligor', label: 'Copy of Invoice Sent to Obligor' },
      {
        value: 'Latest Management Accounts (P&L, Balance Sheet, Cashflow)',
        label: 'Latest Management Accounts (P&L, Balance Sheet, Cashflow)',
      },
      { value: 'Club Bank Statements (Last 3-6 months)', label: 'Club Bank Statements (Last 3-6 months)' },
      { value: 'TMS (Transfer Matching System) Report', label: 'TMS (Transfer Matching System) Report' },
      { value: 'ITC (International Transfer Certificate)', label: 'ITC (International Transfer Certificate)' },
      {
        value: 'Debt Schedule / Statement of Outstanding Liabilities',
        label: 'Debt Schedule / Statement of Outstanding Liabilities',
      },
      { value: 'Forecast Cashflow Model', label: 'Forecast Cashflow Model' },
      { value: 'Promissory Notes', label: 'Promissory Notes' },
    ],
  },
  {
    label: 'Corporate Authorisations',
    options: [
      { value: 'Board Resolution / Power of Attorney', label: 'Board Resolution / Power of Attorney' },
      { value: 'Certificate of Incorporation / Registration', label: 'Certificate of Incorporation / Registration' },
      { value: 'Updated Constitutional Documents', label: 'Updated Constitutional Documents' },
      {
        value: 'Specimen Signatures of Authorised Signatories',
        label: 'Specimen Signatures of Authorised Signatories',
      },
      {
        value: 'Shareholding / Ownership Structure Confirmation',
        label: 'Shareholding / Ownership Structure Confirmation',
      },
    ],
  },
  {
    label: 'Legal & Compliance',
    options: [
      { value: 'Legal Opinion (if required)', label: 'Legal Opinion (if required)' },
      { value: 'KYC / AML Documentation', label: 'KYC / AML Documentation' },
      { value: 'Sanctions & PEP Screening', label: 'Sanctions & PEP Screening' },
      { value: 'Tax Compliance Certificate', label: 'Tax Compliance Certificate' },
      { value: 'Notarisation Certificate', label: 'Notarisation Certificate' },
    ],
  },
  {
    label: 'Collateral & Security',
    options: [
      { value: 'Security Perfection Evidence', label: 'Security Perfection Evidence' },
      { value: 'Insurance Certificates (if applicable)', label: 'Insurance Certificates (if applicable)' },
      { value: 'Controlled Account Setup', label: 'Controlled Account Setup' },
    ],
  },
  {
    label: 'Operational & Systemic',
    options: [
      {
        value: 'Data Verification Report (from SportsFi Platform)',
        label: 'Data Verification Report (from SportsFi Platform)',
      },
      { value: 'Compliance Checklist Signed by Borrower', label: 'Compliance Checklist Signed by Borrower' },
      { value: 'Bank Account Confirmation Letter', label: 'Bank Account Confirmation Letter' },
      { value: 'Confirmation of No Default / Adverse Change', label: 'Confirmation of No Default / Adverse Change' },
      { value: 'KYC / AML Clearance from Funder', label: 'KYC / AML Clearance from Funder' },
      { value: 'Fee Letter / Commitment Fee Payment', label: 'Fee Letter / Commitment Fee Payment' },
    ],
  },
  {
    label: 'Post-Closing / Deferred CPs',
    options: [
      {
        value: 'Assignment Registration (if delayed jurisdictionally)',
        label: 'Assignment Registration (if delayed jurisdictionally)',
      },
      { value: 'Delivery of Certified Originals', label: 'Delivery of Certified Originals' },
    ],
  },
];

export const projectDetailsExpandableOptions: ExpandableCategory[] = [
  {
    name: 'Stadium Project Details',
    expandable: true,
    options: [
      'Overview of project scope, design, and capacity',
      'Construction timeline and key milestones',
      'Total budget breakdown (land, construction, fit-out, contingencies)',
      'List of contractors, architects, and project managers',
      'Status of permits and planning approvals',
      'ESG and sustainability initiatives',
    ],
  },
  {
    name: 'Future Financial Projections',
    expandable: true,
    options: [
      'Revenue and expense projections with assumptions (matchday, sponsorship, retail, etc.)',
      'Expected uplift from new stadium',
      'Cash flow forecasts',
      'Debt servicing capacity and repayment plan',
      'Sensitivity and break-even analysis',
    ],
  },
  {
    name: 'Debt and Financing Plans',
    expandable: true,
    options: [
      'Proposed financing structure (equity vs. debt)',
      'Sources of funds (loans, grants, sponsor contributions, etc.)',
      'Key terms of financing (interest, duration, repayment)',
      'Security/collateral offered (ticketing, naming rights, etc.)',
      'Club credit rating (if applicable)',
    ],
  },
  {
    name: 'Revenue Generation Plans',
    expandable: true,
    options: [
      'Ticketing and pricing strategy',
      'Sponsorship and naming rights (existing or pipeline)',
      'Hospitality, retail, concessions, events',
      'Media and broadcasting impact',
    ],
  },
  {
    name: 'Operational Considerations',
    expandable: true,
    options: [
      'Stadium operations model',
      'Maintenance cost estimates',
      'Fan experience and accessibility plans',
      'Safety, security, and technology strategy',
    ],
  },
  {
    name: 'Legal and Compliance',
    expandable: true,
    options: [
      'Ongoing/pending legal matters',
      'Key contracts (sponsorships, suppliers, media rights)',
      'Insurance coverage',
    ],
  },
  {
    name: 'Stakeholder Engagement',
    expandable: true,
    options: [
      'Fan and community engagement initiatives',
      'Government or municipal support',
      'Strategic partners or potential co-investors',
    ],
  },
  {
    name: 'ESG Considerations',
    expandable: true,
    options: ['Environmental impact plan', 'Community and social programs', 'Governance and transparency policies'],
  },
  {
    name: 'Rating Agency Preparation',
    expandable: true,
    options: [
      'Documentation aligned to credit rating criteria',
      'Financial stress testing',
      'Liquidity and capital adequacy analysis',
      'Benchmark comparisons',
    ],
  },
];

export function getProjectDetailsExpandableOptions(translate: any): ExpandableCategory[] {
  return [
    {
      name: translate('projectDetailsExpandableOptions.stadiumProjectDetails.label'),
      expandable: true,
      options: [
        translate('projectDetailsExpandableOptions.stadiumProjectDetails.options.scopeDesignCapacity'),
        translate('projectDetailsExpandableOptions.stadiumProjectDetails.options.constructionTimeline'),
        translate('projectDetailsExpandableOptions.stadiumProjectDetails.options.budgetBreakdown'),
        translate('projectDetailsExpandableOptions.stadiumProjectDetails.options.contractorsArchitects'),
        translate('projectDetailsExpandableOptions.stadiumProjectDetails.options.permitsApprovals'),
        translate('projectDetailsExpandableOptions.stadiumProjectDetails.options.esgInitiatives'),
      ],
    },
    {
      name: translate('projectDetailsExpandableOptions.futureFinancialProjections.label'),
      expandable: true,
      options: [
        translate('projectDetailsExpandableOptions.futureFinancialProjections.options.revenueExpenseProjections'),
        translate('projectDetailsExpandableOptions.futureFinancialProjections.options.upliftFromStadium'),
        translate('projectDetailsExpandableOptions.futureFinancialProjections.options.cashFlowForecasts'),
        translate('projectDetailsExpandableOptions.futureFinancialProjections.options.debtServicingPlan'),
        translate('projectDetailsExpandableOptions.futureFinancialProjections.options.sensitivityBreakEven'),
      ],
    },
    {
      name: translate('projectDetailsExpandableOptions.debtAndFinancingPlans.label'),
      expandable: true,
      options: [
        translate('projectDetailsExpandableOptions.debtAndFinancingPlans.options.financingStructure'),
        translate('projectDetailsExpandableOptions.debtAndFinancingPlans.options.sourcesOfFunds'),
        translate('projectDetailsExpandableOptions.debtAndFinancingPlans.options.keyTerms'),
        translate('projectDetailsExpandableOptions.debtAndFinancingPlans.options.securityCollateral'),
        translate('projectDetailsExpandableOptions.debtAndFinancingPlans.options.clubCreditRating'),
      ],
    },
    {
      name: translate('projectDetailsExpandableOptions.revenueGenerationPlans.label'),
      expandable: true,
      options: [
        translate('projectDetailsExpandableOptions.revenueGenerationPlans.options.ticketingStrategy'),
        translate('projectDetailsExpandableOptions.revenueGenerationPlans.options.sponsorshipPipeline'),
        translate('projectDetailsExpandableOptions.revenueGenerationPlans.options.hospitalityRetail'),
        translate('projectDetailsExpandableOptions.revenueGenerationPlans.options.mediaImpact'),
      ],
    },
    {
      name: translate('projectDetailsExpandableOptions.operationalConsiderations.label'),
      expandable: true,
      options: [
        translate('projectDetailsExpandableOptions.operationalConsiderations.options.operationsModel'),
        translate('projectDetailsExpandableOptions.operationalConsiderations.options.maintenanceEstimates'),
        translate('projectDetailsExpandableOptions.operationalConsiderations.options.fanExperience'),
        translate('projectDetailsExpandableOptions.operationalConsiderations.options.safetySecurityTech'),
      ],
    },
    {
      name: translate('projectDetailsExpandableOptions.legalAndCompliance.label'),
      expandable: true,
      options: [
        translate('projectDetailsExpandableOptions.legalAndCompliance.options.ongoingLegalMatters'),
        translate('projectDetailsExpandableOptions.legalAndCompliance.options.keyContracts'),
        translate('projectDetailsExpandableOptions.legalAndCompliance.options.insuranceCoverage'),
      ],
    },
    {
      name: translate('projectDetailsExpandableOptions.stakeholderEngagement.label'),
      expandable: true,
      options: [
        translate('projectDetailsExpandableOptions.stakeholderEngagement.options.fanCommunityEngagement'),
        translate('projectDetailsExpandableOptions.stakeholderEngagement.options.governmentSupport'),
        translate('projectDetailsExpandableOptions.stakeholderEngagement.options.strategicPartners'),
      ],
    },
    {
      name: translate('projectDetailsExpandableOptions.esgConsiderations.label'),
      expandable: true,
      options: [
        translate('projectDetailsExpandableOptions.esgConsiderations.options.environmentalImpact'),
        translate('projectDetailsExpandableOptions.esgConsiderations.options.communityPrograms'),
        translate('projectDetailsExpandableOptions.esgConsiderations.options.governanceTransparency'),
      ],
    },
    {
      name: translate('projectDetailsExpandableOptions.ratingAgencyPreparation.label'),
      expandable: true,
      options: [
        translate('projectDetailsExpandableOptions.ratingAgencyPreparation.options.documentationCreditCriteria'),
        translate('projectDetailsExpandableOptions.ratingAgencyPreparation.options.financialStressTesting'),
        translate('projectDetailsExpandableOptions.ratingAgencyPreparation.options.liquidityCapitalAnalysis'),
        translate('projectDetailsExpandableOptions.ratingAgencyPreparation.options.benchmarkComparisons'),
      ],
    },
  ];
}

export function getSecurityRequiredCategories(contractType?: ContractType): ExpandableCategory[] {
  if (contractType == null) {
    throw new Error('contractType is required to get security required categories');
  }

  if (
    [
      ContractType.PlayerTransferFinancing,
      ContractType.PayableFinancing,
      ContractType.MediaRightsFinancing,
      ContractType.SellOnFeesFinancing,
    ].includes(contractType)
  ) {
    return securityExpandableOptionsShort;
  }

  if (
    [
      ContractType.TermLoanFinancing,
      ContractType.BridgeLoanFinancing,
      ContractType.GeneralApplicationFinancing,
      ContractType.GeneralEnquiryFinancing,
    ].includes(contractType)
  ) {
    return securityExpandableOptionsLong;
  }

  throw new Error('Unsupported contract type for security required categories');
}

export function getSecurityBasisOptions(contractType?: ContractType) {
  if (contractType == null) {
    throw new Error('contractType is required to get security basis options');
  }

  if (
    [
      ContractType.PlayerTransferFinancing,
      ContractType.PayableFinancing,
      ContractType.MediaRightsFinancing,
      ContractType.SellOnFeesFinancing,
    ].includes(contractType)
  ) {
    return securityBasisShort;
  }

  if (
    [
      ContractType.TermLoanFinancing,
      ContractType.BridgeLoanFinancing,
      ContractType.GeneralApplicationFinancing,
      ContractType.GeneralEnquiryFinancing,
    ].includes(contractType)
  ) {
    return securityBasisLong;
  }

  throw new Error('Unsupported contract type for security basis options');
}

export function getFundingStructureOptions(contractType?: ContractType) {
  if (contractType == null) {
    throw new Error('contractType is required to get funding structure options');
  }

  if (
    [
      ContractType.PlayerTransferFinancing,
      ContractType.PayableFinancing,
      ContractType.MediaRightsFinancing,
      ContractType.SellOnFeesFinancing,
    ].includes(contractType)
  ) {
    return fundingStructureOptionsShort;
  }

  if (
    [
      ContractType.TermLoanFinancing,
      ContractType.BridgeLoanFinancing,
      ContractType.GeneralApplicationFinancing,
      ContractType.GeneralEnquiryFinancing,
    ].includes(contractType)
  ) {
    return fundingStructureOptionsLong;
  }

  throw new Error('Unsupported contract type for funding structure options');
}

export function getTenorOptions(contractType?: ContractType) {
  if (contractType == null) {
    throw new Error('contractType is required to get tenor options');
  }

  if (contractType === ContractType.BridgeLoanFinancing) {
    return tenorOptionsMonths;
  }

  if (
    [
      ContractType.TermLoanFinancing,
      ContractType.PlayerTransferFinancing,
      ContractType.PayableFinancing,
      ContractType.SellOnFeesFinancing,
      ContractType.MediaRightsFinancing,
    ].includes(contractType)
  ) {
    return tenorOptionsYears;
  }

  if (contractType === ContractType.GeneralApplicationFinancing || ContractType.GeneralEnquiryFinancing) {
    return [...tenorOptionsMonths, ...tenorOptionsYears];
  }

  throw new Error('Unsupported contract type for tenor options');
}
